<template>
  <div>
    <div class="d-flex align-center" style="min-height: 40px">
      <b>{{ $t('PROJECT_SHEET.LINKS') }}</b>
      <v-btn-icon v-if="userIsProjectAdmin" size="small" class="ml-auto export-hidden" @click="isEditing = ! isEditing">
        <v-icon>{{ isEditing ? 'far fa-close' : 'far fa-pencil-alt' }}</v-icon>
      </v-btn-icon>
    </div>

    <v-card class="pa-6 d-flex flex-column">
      <div v-if="isEditing">
        <draggable v-if="files.length" :list="files" item-key="id" handle=".checklist-draggable-anchor" class="mb-6" @update="saveFiles">
          <template #item="{ element: link }">
            <div class="d-flex align-center">
              <v-icon size="small" class="checklist-draggable-anchor grab text-medium-emphasis mr-2">far fa-grip-vertical</v-icon>
              <span class="text-ellipsis">
                <link-icon :link="link" class="mr-2"></link-icon>
                <a :href="subdomainLinkUrl(link.url)" :title="link.name" target="_blank" rel="noopener" @click.stop>
                  {{ link.name || link.display_filename }}
                </a>

                <v-menu v-if="isPicture(link)" content-class="ml-2 rounded-sm" open-on-hover location="left" offset="8" transition="scale-transition" activator="parent">
                  <div class="bg-surface" style="max-width: 400px">
                    <img v-if="link.url" :src="link.url" :alt="$t('FILES.LINK_IMAGE_UNAVAILABLE')" style="width: 100%;">
                  </div>
                </v-menu>
              </span>

              <template v-if="userIsProjectAdmin">
                <v-btn-icon size="small" class="ml-auto" @click="editLinkName(link)">
                  <v-icon>far fa-pencil-alt</v-icon>
                  <v-tooltip location="bottom" activator="parent">{{ $t('GLOBAL.RENAME') }}</v-tooltip>
                </v-btn-icon>
                <v-btn-icon size="small" color="errorred" class="ml-1" @click="deleteFile(link)">
                  <v-icon>far fa-trash-alt</v-icon>
                  <v-tooltip location="bottom" activator="parent">{{ $t('GLOBAL.DELETE') }}</v-tooltip>
                </v-btn-icon>
              </template>
            </div>
          </template>
        </draggable>

        <!-- FILE DROP ZONE -->
        <file-upload v-if="userIsProjectAdmin && isPremium && ! linksConfig.hideFileUpload" ref="fileUpload" input-id="file-project-sheet" class="export-hidden" @uploaded="fileUploaded"></file-upload>
        <div v-if="! isPremium" class="file-upload">
          <div class="filedropbox" style="cursor: default">
            <span class="text-disabled"><i class="far fa-plus-circle"></i>&nbsp; {{ $t('PROJECT_SHEET.ADD_FILE') }}</span>
            <div><a :href="$t('PREMIUM.OFFERS_LINK')" target="_blank" rel="noopener"><b>{{ $t('PREMIUM.PREMIUM_FEATURE') }}</b></a></div>
          </div>
        </div>

        <!-- ADD LINK FORM-->
        <form v-if="userIsProjectAdmin" class="export-hidden mt-2" @submit.prevent="addLink()">
          <field-label :label="$t('ELEMENT-DETAIL.ADD_LINK')">
            <template #default="{ id }">
              <div class="d-flex align-center">
                <v-text-field v-model="linkurl" :id="id" placeholder="https://..." density="compact"></v-text-field>
                <v-btn :disabled="! linkurl" color="primary" rounded type="submit" class="ml-2">{{ $t('ELEMENT-DETAIL.ADD') }}</v-btn>
              </div>
            </template>
          </field-label>
        </form>
      </div>

      <!-- DISPLAY MODE -->
      <div v-else class="flex-grow-1 d-flex flex-column gap-4">
        <div v-if="files.length" class="d-flex flex-column gap-2">
          <div v-for="link in files" class="d-flex align-center">
            <div v-if="link.url && isPicture(link)" class="flex-center-center" style="width: 3em; height: 3em; flex: 0 0 3em">
              <img :src="link.url" :alt="$t('FILES.LINK_IMAGE_UNAVAILABLE')" class="rounded" style="width: 100%; height: 100%; object-fit: cover">
              <v-menu content-class="ml-2 rounded-sm" open-on-hover location="right" offset="8" transition="scale-transition" activator="parent">
                <div class="bg-surface" style="max-width: 400px">
                  <img v-if="link.url" :src="link.url" :alt="$t('FILES.LINK_IMAGE_UNAVAILABLE')" style="width: 100%;">
                </div>
              </v-menu>
            </div>
            <div v-else :style="{ backgroundColor: `${linkColor(link)}66`, color: linkColor(link) }" class="flex-center-center rounded" style="width: 3em; height: 3em; flex: 0 0 3em">
              <link-icon :link="link" style="font-size: 1.5em"></link-icon>
            </div>
            <a :title="link.name" :href="subdomainLinkUrl(link.url)" target="_blank" rel="noopener" class="px-2 small line-height-comfortable" style="color: inherit; line-break: anywhere">
              {{ linkDisplayName(link) }}
            </a>
          </div>
        </div>
        <div v-else class="text-center text-medium-emphasis">{{ $t('PROJECT_SHEET.NO_FILE') }}</div>

        <div class="text-center mt-auto export-hidden">
          <v-btn variant="text" @click="$emit('open-project-report', 'files')">
            {{ $t('PROJECT_SHEET.SEE_ALL_FILES') }}
            <v-icon size="small" class="ml-1">far fa-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
  import draggable from 'vuedraggable';
  import clientConfig from '@/client_customs/config';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import FileUpload from '@/components/Reusables/FileUpload.vue';
  import LinkIcon from '@/components/Planning/partials/LinkIcon.vue';

  function deepcopy(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  export default {
    components: {
      draggable,
      FileUpload,
      LinkIcon,
    },
    props: {
      planning: { type: Object, required: true },
      closeEditing: { type: Boolean, default: false },
    },
    emits: ['update', 'open-project-report'],
    data() {
      const projectsheet = this.planning.projectsheet || {};
      const userIsProjectAdmin = this.planning.meta.access_right == 'admin';

      return {
        userIsProjectAdmin,
        files: deepcopy(projectsheet.files || []),
        linkurl: "",
        isEditing: false,
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      categoriesColors() {
        return {
          image: null,
          video: this.$vuetify.theme.current.colors.errorred,
          audio: this.$vuetify.theme.current.colors.info,
          pdf: this.$vuetify.theme.current.colors.errorred,
          spreadsheet: this.$vuetify.theme.current.colors.success,
          word: this.$vuetify.theme.current.colors.primary,
          archive: this.$vuetify.theme.current.colors.warning,
          file: this.$vuetify.theme.current.colors.midgrey,
          link: this.$vuetify.theme.current.colors.primary,
          onedrive: this.$vuetify.theme.current.colors.info,
          googledrive: this.$vuetify.theme.current.colors.info,
          dropbox: this.$vuetify.theme.current.colors.info,
        };
      },
      linksConfig() {
        return this.usersAccessRightStore.config.links;
      },
      isPremium() { return this.usersAccessRightStore.isPremium; },
    },
    watch: {
      closeEditing(newVal) {
        if (newVal) this.isEditing = false;
      },
    },
    methods: {
      isPicture(link) {
        if (link.mime && link.mime.includes('image')) return true;
        return /(http)s?:(\/\/[^"']*\.(?:png|jpg|jpeg|gif|png|svg)$)/i.test(link.url);
      },
      subdomainLinkUrl(url) {
        return clientConfig.subdomain && url?.startsWith('https://api.bubbleplan.net') ? `${url}?subdomain=${clientConfig.subdomain}` : url;
      },
      linkDisplayName(link) {
        const name = link.name || link.display_filename;
        if (name.length > 60) return `${name.substr(0, 40) }...${ name.substr(name.length - 15)}`;
        return name;
      },
      linkColor(link) {
        const category = link.mime && this.$filters.mimeIcon(link.mime).replace(/^(far|fas|fab) fa-/, '') || link.type || 'file';
        return this.categoriesColors[category] || null;
      },
      saveFiles() {
        this.$emit('update', {
          files: deepcopy(this.files),
        });
      },
      fileUploaded(newFile) {
        if (! (newFile && newFile.response && newFile.response.file)) return;
        this.files.push(newFile.response.file);
        this.saveFiles();
      },
      addLink() {
        if (! this.linkurl) return;
        let name = this.linkurl.replace(/\/+$/, ''); // strip ending slashes
        name = name.substr(name.lastIndexOf('/') + 1);
        this.files.push({ type: 'link', url: this.linkurl, name });
        this.linkurl = "";
        this.saveFiles();
      },
      editLinkName(link) {
        const newName = window.prompt(this.$t('GLOBAL.RENAME'), link.name || link.display_filename);
        if (! newName) return;
        link.name = newName;
        this.saveFiles();
      },
      deleteFile(file) {
        const index = this.files.indexOf(file);
        if (index == -1) return;
        this.files.splice(index, 1);
        this.saveFiles();
      },
    },
  };
</script>
